import { GlobalCacheConfig, LocalStorageStrategy } from "ts-cacheable";
import { get as _get } from "lodash";
const stringify = require("fast-json-stable-stringify");

export const CACHE_KEYS = {
  flipcards: 'flipcards',
  levels: 'levels',
  points: 'points',
  info: 'info',
  info_v2: 'info_v2',
  "commissions-agent": 'commissions-agent',
  "commissions-admin": 'commissions-admin',
  questioner: 'questioner',
  getAvailableProducts: 'getAvailableProducts',
  orders: 'orders',
  rewards: 'rewards',
  availableVisas: 'availableVisas',
  wizardsLeads: 'wizardsLeads',
  wizardsCount: 'wizardsCount',
};

export const CacheHelper = (() => {
  const cacheKey = "CACHE_STORAGE";
  const defaultCacheTTL = 15000; // 15s
  const cacheMaxCacheCount = 7; // Handles 7 same domain requests with different params

  const customTTLs: Record<string, number> = {
    [CACHE_KEYS.rewards]: 30 * 1000, // 30 seconds
    [CACHE_KEYS.orders]: 30 * 1000, // 30 seconds
    [CACHE_KEYS.flipcards]: 30 * 60 * 1000, // 30 minutes
    [CACHE_KEYS.levels]: 12 * 60 * 60 * 1000, // 12 hours
    [CACHE_KEYS.points]: 30 * 60 * 1000, // 30 minutes
    [CACHE_KEYS["commissions-agent"]]: 30 * 60 * 1000, // 30 minutes
    [CACHE_KEYS["commissions-admin"]]: 30 * 60 * 1000, // 30 minutes
    [CACHE_KEYS.questioner]: 5 * 60 * 1000, // 5 minutes
    [CACHE_KEYS.getAvailableProducts]: 30 * 60 * 1000, // 30 minutes
    [CACHE_KEYS.availableVisas]: 60 * 60 * 1000, // 1 hour
  };

  return {
    cacheKey: cacheKey,
    init: () => {
      GlobalCacheConfig.maxAge = defaultCacheTTL;
      GlobalCacheConfig.storageStrategy = LocalStorageStrategy;
      GlobalCacheConfig.cacheHasher = (p: any[]) => stringify(p);
      GlobalCacheConfig.maxCacheCount = cacheMaxCacheCount;
      GlobalCacheConfig.globalCacheKey = cacheKey;
    },
    clearExpired: () => {
      try {
        const cache = JSON.parse(localStorage.getItem(cacheKey) || "{}");
        let removed = 0;

        Object.keys(cache).forEach((methodKey) => {
          const currentTTL = customTTLs[methodKey] || defaultCacheTTL;
          if (Array.isArray(cache[methodKey])) {
            cache[methodKey] = cache[methodKey].filter((entry) => {
              const createdTime = new Date(entry.created).getTime();
              const isExpired = Date.now() - createdTime > currentTTL;
              if (isExpired) removed++;
              return !isExpired;
            });

            if (cache[methodKey].length === 0) {
              delete cache[methodKey];
            }
          }
        });

        localStorage.setItem(cacheKey, JSON.stringify(cache));
        console.info(`[CacheHelper] Cleared ${removed} expired cache entries`);
      } catch (err) {
        console.warn("[CacheHelper] Failed to clear expired cache", err);
      }
    },
    removeKey: (methodKey: string) => {
      try {
        const cache = JSON.parse(localStorage.getItem("CACHE_STORAGE") || "{}");
        if (cache[methodKey]) {
          delete cache[methodKey];
          localStorage.setItem("CACHE_STORAGE", JSON.stringify(cache));
          console.info(`[CacheHelper] Key "${methodKey}" removed from cache`);
        }
      } catch (err) {
        console.warn("[CacheHelper] Failed to remove key", err);
      }
    },
    removeKeyEntries: (methodKey: string, predicate: (params: any[]) => boolean) => {
      try {
        const cache = JSON.parse(localStorage.getItem(cacheKey) || "{}");
        if (cache[methodKey] && Array.isArray(cache[methodKey])) {
          const before = cache[methodKey].length;
          cache[methodKey] = cache[methodKey].filter((entry) => {
            try {
              const params = JSON.parse(entry.parameters);
              return !predicate(params);
            } catch {
              return true;
            }
          });
          if (cache[methodKey].length === 0) {
            delete cache[methodKey];
          }
          localStorage.setItem(cacheKey, JSON.stringify(cache));
          const removed = before - _get(cache, [methodKey, 'length'], 0);
          console.info(`[CacheHelper] Removed ${removed} entries from "${methodKey}"`);
        }
      } catch (err) {
        console.warn("[CacheHelper] Failed to remove cache entries", err);
      }
    },
  };
})();
